// skipcq: JS-W1028, JS-0128
import React, { useState, useCallback, useEffect } from 'react';
import PropTypes from 'prop-types';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Button from '@mui/material/Button';
import Box from '@mui/material/Box';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import Radio from '@mui/material/Radio';
import CircularProgress from '@mui/material/CircularProgress';
import Typography from '@mui/material/Typography';
import { formatEventDateTimeLabel } from '../../domain/eventTimezones';

function SelectableEventRow({ event, isChecked, onToggleEvent }) {
  const eventId = event.id;
  const dateLabel = formatEventDateTimeLabel(
    event.eventLocalDate,
    event.eventTimezone,
  );
  const host = String(event.host ?? '').trim();
  const secondary = [dateLabel, host].filter(Boolean).join(' · ');

  const handleRowActivate = useCallback(() => {
    onToggleEvent(eventId);
  }, [eventId, onToggleEvent]);

  return (
    <ListItem disablePadding>
      <ListItemButton
        dense
        onClick={handleRowActivate}
        selected={isChecked}
      >
        <ListItemIcon sx={{ minWidth: 42 }}>
          <Radio
            edge="start"
            size="small"
            tabIndex={-1}
            disableRipple
            name="add-beers-to-event"
            value={eventId}
            checked={isChecked}
            inputProps={{
              'aria-labelledby': `event-option-${eventId}`,
              readOnly: true,
            }}
          />
        </ListItemIcon>
        <ListItemText
          id={`event-option-${eventId}`}
          primary={event.name || 'Untitled event'}
          secondary={secondary || undefined}
        />
      </ListItemButton>
    </ListItem>
  );
}

SelectableEventRow.propTypes = {
  event: PropTypes.shape({
    id: PropTypes.string.isRequired,
    name: PropTypes.string,
    host: PropTypes.string,
    eventLocalDate: PropTypes.string,
    eventTimezone: PropTypes.string,
  }).isRequired,
  isChecked: PropTypes.bool.isRequired,
  onToggleEvent: PropTypes.func.isRequired,
};

/**
 * Pick one event and add selected beer IDs to its featuredBeers list.
 */
function AddBeersToEventDialog({
  open,
  onClose,
  events,
  eventsLoading,
  eventsError = null,
  selectedBeerIds,
  onConfirmAdd,
}) {
  const [selectedEventId, setSelectedEventId] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (open) {
      setSelectedEventId(null);
      setSubmitting(false);
    }
  }, [open]);

  const handleToggleEvent = useCallback(
    (eventId) => {
      setSelectedEventId((current) => (current === eventId ? null : eventId));
    },
    [],
  );

  const handleAddBeers = useCallback(async () => {
    if (!selectedEventId || !selectedBeerIds.length) return;
    setSubmitting(true);
    try {
      await onConfirmAdd(selectedEventId, selectedBeerIds);
      onClose();
    } catch {
      // Parent shows error; keep dialog open for retry
    } finally {
      setSubmitting(false);
    }
  }, [selectedEventId, selectedBeerIds, onConfirmAdd, onClose]);

  return (
    <Dialog open={open} onClose={submitting ? undefined : onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Add beers to event</DialogTitle>
      <DialogContent dividers>
        {eventsLoading ? (
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              py: 3,
            }}
          >
            <CircularProgress size={32} />
          </Box>
        ) : eventsError ? (
          <Typography variant="body2" color="error" role="alert">
            {eventsError}
          </Typography>
        ) : !events.length ? (
          <Typography variant="body2" color="text.secondary">
            No events available. Create an event in Events first.
          </Typography>
        ) : (
          <List disablePadding>
            {events.map((event) => (
              <SelectableEventRow
                key={event.id}
                event={event}
                isChecked={selectedEventId === event.id}
                onToggleEvent={handleToggleEvent}
              />
            ))}
          </List>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={submitting}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleAddBeers}
          disabled={
            submitting
            || !selectedEventId
            || !selectedBeerIds.length
            || eventsLoading
            || Boolean(eventsError)
            || !events.length
          }
        >
          {submitting ? 'Adding…' : 'Add Beers'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

AddBeersToEventDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  events: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      name: PropTypes.string,
      host: PropTypes.string,
      eventLocalDate: PropTypes.string,
      eventTimezone: PropTypes.string,
    }),
  ).isRequired,
  eventsLoading: PropTypes.bool.isRequired,
  eventsError: PropTypes.string,
  selectedBeerIds: PropTypes.arrayOf(PropTypes.string).isRequired,
  onConfirmAdd: PropTypes.func.isRequired,
};

export default AddBeersToEventDialog;
